import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
    MdExpandLess,
    MdExpandMore,
    MdOndemandVideo,
    MdOutlineArrowBack
} from 'react-icons/md';
import {
    FaCheck,
    FaExclamation,
    FaPlay,
    FaPlayCircle
} from 'react-icons/fa';
import { RiMoreFill, RiSortDesc } from 'react-icons/ri';
import moment from 'moment';
import Player from '@vimeo/player';
import axios from 'config/axios';
import { useAppSelector } from 'hooks/useStore';
import Loader from 'components/Loader';
import { createTimerWorker } from 'workers/createTimerWorker';

import './index.css';

function Lectures() {
    const [courseName, setCourseName] = useState('');
    const [lectureGroups, setLectureGroups] = useState(null);
    const [lectureGroupOpen, setLectureGroupOpen] = useState([]);
    const [player, setPlayer] = useState(null);
    const [currentLecture, setCurrentLecture] = useState(null);
    const [previousLecture, setPreviousLecture] = useState(null);
    const [viewTime, setViewTime] = useState(0);
    const [progress, setProgress] = useState(0);
    const [scrollPosition, setScrollPosition] = useState(0);
    const [maxScrollHeight, setMaxScrollHeight] = useState(0);
    const [sorted, setSorted] = useState(true);

    const lecturesRef = useRef(null);
    const timerWorkerRef = useRef(null);
    const lectureGroupsRef = useRef(null);
    const currentLectureRef = useRef(null);
    const previousLectureRef = useRef(null);
    const progressRef = useRef(0);
    const viewTimeRef = useRef(0);

    const navigate = useNavigate();
    const { courseUrl } = useParams();
    const { organization } = useAppSelector(state => state.organization);
    
    const handleScroll = () => {
        if (window.scrollY < maxScrollHeight) {
            setScrollPosition(window.scrollY);
        }
    }

    const updateLectureProgress = (lecture) => {
        const currentProgress = progressRef.current;
        const currentViewTime = viewTimeRef.current;
        
        if (currentProgress > 0 && currentViewTime > 0) {
            axios.post(`courses/courses/${organization.urlSlug}/${courseUrl}/watch/`, {
                    lectureId: lecture.id,
                    progress: currentProgress,
                    viewTime: currentViewTime
                })
                .then(res => {
                    setProgress(0);
                    setViewTime(0);
                })
                .catch(error => {
                    console.log(error);
                });
        }
    }

    const updateCurrentLecture = (lecture) => {
        // updateLectureProgress(currentLecture);

        // setCurrentLecture(lecture);

        // if (player) {
        //     player.pause().then(() => {
        //         setCurrentLecture(lecture);
        //     });
        // }
        // else {            
        //     setCurrentLecture(lecture);
        // }
        const currLecture = currentLectureRef.current;
        const currProgress = progressRef.current;
        const currViewTime = viewTimeRef.current;
        
        if (currLecture && currProgress > 0 && currViewTime > 0) {
            axios.post(`courses/courses/${organization.urlSlug}/${courseUrl}/watch/`, {
                    lectureId: currLecture.id,
                    progress: currProgress,
                    viewTime: currViewTime
                })
                .then(res => {
                    setProgress(0);
                    setViewTime(0);
                    setCurrentLecture(lecture);
                })
                .catch(error => {
                    console.log(error);
                });
        }
        else {
            setCurrentLecture(lecture);
        }
    }

    useEffect(() => {
        axios.get(`courses/courses/${organization.urlSlug}/${courseUrl}/`)
            .then(res => {
                // console.log(res.data);
                setCourseName(res.data.courseName);
                setLectureGroups(res.data.lectureGroups);
                setLectureGroupOpen(new Array(res.data.lectureGroups.length).fill(false));
            })
            .catch(error => {
                console.log(error);
            });
    }, []);

    useEffect(() => {
        if (currentLecture === null) return;

        if (player) {
            player.loadVideo(currentLecture.videoUrl);
        }
        else {
            setPlayer(new Player('vimeoPlayer', {
                url: currentLecture.videoUrl
            }));
        }
        if (window.innerWidth <= 1024) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }

        if (timerWorkerRef.current) {
            timerWorkerRef.current.postMessage({ type: 'stop' });
            timerWorkerRef.current.terminate();
            timerWorkerRef.current = null;
        }

        timerWorkerRef.current = createTimerWorker();

        timerWorkerRef.current.onmessage = (event) => {
            if (event.data === 'tick') {
                setViewTime(prev => prev + 1);
            }
        }

        return () => {
            if (timerWorkerRef.current) {
                timerWorkerRef.current.postMessage({ type: 'stop' });
                timerWorkerRef.current.terminate();
                timerWorkerRef.current = null;
            }
        }
    }, [currentLecture]);

    useEffect(() => {
        if (!player) return;

        const onLoaded = () => {
            player.getDuration().then(duration => {
                let startTimeInSeconds = duration * currentLectureRef.current.progress;

                player.setCurrentTime(startTimeInSeconds);
            });
        }
        const onPlaying = () => {
            // console.log('playing');
            timerWorkerRef.current?.postMessage({ type: 'start', delay: 1000 });
            setPreviousLecture(currentLectureRef.current);
        }
        const onPause = () => {
            // console.log('pause');
            timerWorkerRef.current?.postMessage({ type: 'stop' });
            updateLectureProgress(currentLectureRef.current);
            setPreviousLecture(currentLectureRef.current);
        };
        // const onEnded = () => {
        //     console.log('ended');
        //     timerWorkerRef.current?.postMessage({ type: 'stop' });
        //     updateLectureProgress(currentLectureRef.current);
        // };
        const onTimeUpdate = (data) => {
            if (previousLectureRef.current.id !== currentLectureRef.current.id) return;

            let newLectureGroups = lectureGroupsRef.current;

            const lectureGroupToUpdate = newLectureGroups.find(lectureGroup => lectureGroup.lectures.find(lecture => lecture.id === currentLectureRef.current.id));
            const lectureToUpdate = lectureGroupToUpdate.lectures.find(lecture => lecture.id === currentLectureRef.current.id);

            lectureToUpdate.progress = data.percent;
            if (data.percent === 1) {
                lectureToUpdate.completed = true;
            }
            setLectureGroups(newLectureGroups);

            setProgress(data.percent);
        }

        player.on('loaded', onLoaded);
        player.on('playing', onPlaying);
        player.on('pause', onPause);
        // player.on('ended', onEnded);
        player.on('timeupdate', onTimeUpdate);

        return () => {
            player.off('loaded', onLoaded);
            player.off('playing', onPlaying);
            player.off('pause', onPause);
            // player.off('ended', onEnded);
            player.off('timeupdate', onTimeUpdate);
        };
    }, [player]);

    useEffect(() => {
        viewTimeRef.current = viewTime;
    }, [viewTime]);
    
    useEffect(() => {
        progressRef.current = progress;
    }, [progress]);

    useEffect(() => {
        currentLectureRef.current = currentLecture;
    }, [currentLecture]);

    useEffect(() => {
        previousLectureRef.current = previousLecture;
    }, [previousLecture]);

    useEffect(() => {
        lectureGroupsRef.current = lectureGroups;
    }, [lectureGroups]);

    useEffect(() => {
        if (lecturesRef.current) {
            const handleResize = () => {
                const rect = lecturesRef.current.getBoundingClientRect();
                if (window.innerWidth <= 1024) {
                    setMaxScrollHeight(0);
                }
                else {
                    setMaxScrollHeight(rect.height);
                }
            }

            handleResize();

            window.addEventListener('resize', handleResize);
            
            return () => {
                window.removeEventListener('resize', handleResize);
            }
        }
    }, [lectureGroups, lectureGroupOpen]);

    useEffect(() => {
        window.addEventListener('scroll', handleScroll);
        
        return () => {
            window.removeEventListener('scroll', handleScroll);
        }
    }, [maxScrollHeight]);

    if (lectureGroups === null) return <Loader />;

    return (    
        <div className='layout'>
            <Helmet>
                <title>
                    {courseName} Lectures - {organization.name}
                </title>
            </Helmet>
            <button className='lectures__back' onClick={() => navigate('/lectures')}>
                <MdOutlineArrowBack className='lectures__backIcon' />
                Back
            </button>
            <div className='lectures' ref={lecturesRef}>
                <div className='lectures__list'>
                    <div className='lectures__lectureHeader'>
                        <h1 className='lectures__lectureHeaderText'>
                            {courseName} Lectures
                        </h1>
                        <div className='lectures__lectureHeaderRecommendations'>
                            {lectureGroups.some(lectureGroup => lectureGroup.lectures.some(lecture => lecture.critical)) &&
                                <p className='lectures__lectureHeaderRecommendation'>
                                    <FaExclamation className='lectures__lectureGroupCriticalIcon' />
                                    {lectureGroups.reduce((count, lectureGroup) => count + lectureGroup.lectures.filter(lecture => lecture.critical).length, 0)} critical
                                </p>
                            }
                            {lectureGroups.some(lectureGroup => lectureGroup.lectures.some(lecture => lecture.recommended)) &&
                                <p className='lectures__lectureHeaderRecommendation'>
                                    <RiMoreFill className='lectures__lectureGroupRecommendedIcon' />
                                    {lectureGroups.reduce((count, lectureGroup) => count + lectureGroup.lectures.filter(lecture => lecture.recommended && !lecture.critical).length, 0)} advised to review
                                </p>
                            }
                            {/* <button
                                className={sorted ? 'lectures__sortButton--active' : 'lectures__sortButton'}
                                onClick={() => setSorted(prev => !prev)}
                            >
                                Recommended <RiSortDesc className='lectures__sortButtonIcon' />
                            </button> */}
                        </div>
                    </div>
                    {(sorted ? [...lectureGroups].sort((a, b) => (
                        b.weaknessScore - a.weaknessScore
                    )) : lectureGroups).map((lectureGroup, lectureGroupIndex) => (
                        <React.Fragment key={lectureGroup.name}>
                            <div
                                className='lectures__lectureGroup'
                                onClick={() => setLectureGroupOpen(prevLectureGroupOpen => {
                                    let newLectureGroupOpen = [...prevLectureGroupOpen];
                                    newLectureGroupOpen[lectureGroupIndex] = !newLectureGroupOpen[lectureGroupIndex];
                                    return newLectureGroupOpen   
                                })}
                            >
                                <h4 className='lectures__lectureGroupTitle'>
                                    {lectureGroup.groupNumber}. {lectureGroup.name}
                                </h4>
                                <div className='lectures__lectureGroupInfo'>
                                    <p className='lectures__lectureGroupDuration'>
                                        <MdOndemandVideo className='lectures__lectureGroupInfoIcon' />
                                        {lectureGroup.lectures.length} {lectureGroup.lectures.length > 1 
                                            ? 'lectures'
                                            : 'lecture'
                                        }, {Math.floor(moment.duration(lectureGroup.duration).asMinutes())} min
                                    </p>
                                    {lectureGroup.lectures.find(lecture => lecture.critical)
                                        ?   <FaExclamation className='lectures__lectureGroupCriticalIcon' />
                                        :   lectureGroup.lectures.find(lecture => lecture.recommended)
                                        ?   <RiMoreFill className='lectures__lectureGroupRecommendedIcon' />
                                        :   <></>
                                    }
                                    {lectureGroup.lectures.find(lecture => lecture.id === currentLecture?.id)
                                        ?   <p className='lectures__lectureGroupPlaying'>
                                                <FaPlayCircle className='lectures__lectureGroupInfoIcon' />
                                                playing
                                            </p>
                                        :   lectureGroup.lectures.every(lecture => lecture.completed)
                                        ?   <p className='lectures__lectureGroupPlaying'>
                                                <FaCheck className='lectures__lectureGroupInfoIcon' />
                                                completed
                                            </p>
                                        :   <></>
                                    }
                                </div>
                                {lectureGroupOpen[lectureGroupIndex]
                                    ? <MdExpandLess className='lectures__lectureGroupExpandIcon' />
                                    : <MdExpandMore className='lectures__lectureGroupExpandIcon' />
                                }
                            </div>
                            {lectureGroupOpen[lectureGroupIndex] &&
                                <div className='lectures__lectureGroupLectures'>
                                    {lectureGroup.lectures.map(lecture => (
                                        <div
                                            className={currentLecture?.id === lecture.id
                                                ? 'lectures__lecture--current'
                                                : 'lectures__lecture'
                                            }
                                            key={lecture.id}
                                            onClick={() => updateCurrentLecture(lecture)}
                                        >
                                            {lecture.critical
                                                ? <FaExclamation className='lectures__lectureCriticalIcon' />
                                                : lecture.recommended
                                                ? <RiMoreFill className='lectures__lectureRecommendedIcon' />
                                                : <></>
                                            }
                                            <p className='lectures__lectureName'>{lecture.name}</p>
                                            <p className='lectures__lectureDuration'>
                                                <MdOndemandVideo className='lectures__lectureDurationIcon' />
                                                {Math.round(moment.duration(lecture.duration).asMinutes())} min
                                                <div className='lectures__lectureProgress'>
                                                    <div className='lectures__lectureProgressBar' style={{ width: `${lecture.progress*100}%` }}></div>
                                                </div>
                                            </p>
                                            {lecture.completed
                                                ? <FaCheck className='lectures__lectureStatusIcon' />
                                                : <></>
                                            }
                                        </div>
                                    ))}
                                </div>
                            }
                        </React.Fragment>
                    ))}
                </div>
                <div
                    className='lectures__lectureVideo'
                    style={{ 
                        transform: `translateY(${scrollPosition}px)`,
                        transition: 'transform 0.5s cubic-bezier(0.25, 0.1, 0.25, 1)' 
                    }}
                >
                    <div className='lectures__lectureVideoContainer'>
                        <div id='vimeoPlayer'></div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default Lectures;